/* Pareto Analyzer — 80/20 defect prioritisation */
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText } from "lucide-react";

function paretoize(items) {
  const sorted = [...items].sort((a, b) => Number(b.frequency || 0) - Number(a.frequency || 0));
  const total = sorted.reduce((a, x) => a + Number(x.frequency || 0), 0);
  let cum = 0;
  return sorted.map((it) => {
    const f = Number(it.frequency || 0);
    cum += f;
    const cumPct = total ? (cum / total) * 100 : 0;
    return { ...it, _share: total ? (f / total) * 100 : 0, _cum: cumPct, _vital: cumPct <= 80 };
  });
}

function ParetoChart({ rows }) {
  if (rows.length === 0) return <p className="text-xs text-sage-500 italic">Add at least one category to see the Pareto chart.</p>;
  const maxF = Math.max(...rows.map(r => Number(r.frequency || 0)), 1);
  const W = 600, H = 220, P = 40;
  const barW = (W - P * 2) / rows.length * 0.7;
  const cx = (i) => P + (i + 0.5) * ((W - P * 2) / rows.length);
  const linePts = rows.map((r, i) => `${cx(i)},${H - P - (r._cum / 100) * (H - P * 2)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="pareto-chart">
      {/* Axes */}
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#94A3A0" strokeWidth="1" />
      <line x1={P} y1={P / 2} x2={P} y2={H - P} stroke="#94A3A0" strokeWidth="1" />
      <line x1={W - P} y1={P / 2} x2={W - P} y2={H - P} stroke="#94A3A0" strokeWidth="1" />
      <line x1={P} y1={H - P - 0.8 * (H - P * 2)} x2={W - P} y2={H - P - 0.8 * (H - P * 2)} stroke="#DC2626" strokeDasharray="3,3" strokeWidth="0.8" />
      <text x={W - P + 2} y={H - P - 0.8 * (H - P * 2) + 3} fontSize="9" fill="#DC2626">80%</text>
      {/* Bars */}
      {rows.map((r, i) => {
        const h = (Number(r.frequency || 0) / maxF) * (H - P * 2);
        return (
          <g key={i}>
            <rect x={cx(i) - barW / 2} y={H - P - h} width={barW} height={h} fill={r._vital ? "#7C9A7E" : "#C5D6C7"} />
            <text x={cx(i)} y={H - P + 12} textAnchor="middle" fontSize="9" fill="#475569">{(r.label || "").slice(0, 8)}</text>
            <text x={cx(i)} y={H - P - h - 3} textAnchor="middle" fontSize="9" fill="#1E293B" fontWeight="bold">{r.frequency}</text>
          </g>
        );
      })}
      {/* Cumulative line */}
      <polyline points={linePts} fill="none" stroke="#D97706" strokeWidth="2" />
      {rows.map((r, i) => (<circle key={`p-${i}`} cx={cx(i)} cy={H - P - (r._cum / 100) * (H - P * 2)} r="3" fill="#D97706" />))}
    </svg>
  );
}

export default function ParetoWizard({ project, onSave, onExportPDF }) {
  const steps = [
    {
      key: "setup", label: "Setup",
      render: ({ data, updateData }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pareto-setup">
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Defect / problem family</label><input value={data.problem_family || ""} onChange={e => updateData({ problem_family: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Customer complaints (last 90d)" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Period</label><input value={data.period || ""} onChange={e => updateData({ period: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Q1 2026" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Unit (e.g. defects, $, calls)</label><input value={data.unit || "defects"} onChange={e => updateData({ unit: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Data source</label><input value={data.source || ""} onChange={e => updateData({ source: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Salesforce ticket export" /></div>
        </div>
      ),
    },
    {
      key: "categories", label: "Categories",
      render: ({ data, updateData }) => {
        const items = data.categories || [];
        const add = () => updateData({ categories: [...items, { label: "", frequency: 0, note: "" }] });
        const upd = (i, p) => updateData({ categories: items.map((m, idx) => idx === i ? { ...m, ...p } : m) });
        const rm  = (i) => updateData({ categories: items.filter((_, idx) => idx !== i) });
        return (
          <div className="space-y-2" data-testid="pareto-categories">
            <div className="hidden md:grid grid-cols-12 gap-2 px-2 py-1.5 bg-sage-100 rounded-md text-[10px] uppercase tracking-wider font-semibold text-sage-700 border border-sage-200" data-testid="pareto-headers">
              <div className="md:col-span-5">Category / defect type</div>
              <div className="md:col-span-2 text-right pr-2">Frequency</div>
              <div className="md:col-span-4">Note (optional)</div>
              <div className="md:col-span-1 text-center text-sage-400">×</div>
            </div>
            {items.map((it, i) => (
              <div key={i} className="bg-white border border-sage-200 rounded-md p-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`pareto-cat-${i}`}>
                <input data-testid={`pareto-cat-${i}-label`} placeholder="Category / defect type" value={it.label} onChange={e => upd(i, { label: e.target.value })} className="md:col-span-5 border border-sage-200 rounded-md px-2 py-1.5 text-sm" />
                <input data-testid={`pareto-cat-${i}-freq`} type="number" placeholder="Frequency" value={it.frequency} onChange={e => upd(i, { frequency: Number(e.target.value) })} className="md:col-span-2 border border-sage-200 rounded-md px-2 py-1.5 text-sm font-mono text-right" />
                <input placeholder="Note" value={it.note || ""} onChange={e => upd(i, { note: e.target.value })} className="md:col-span-4 border border-sage-100 rounded-md px-2 py-1.5 text-xs text-sage-600" />
                <button onClick={() => rm(i)} className="text-red-500 justify-self-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button data-testid="pareto-add-cat" onClick={add} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-3 py-2 text-sm hover:bg-sage-50 w-full justify-center"><Plus className="w-3.5 h-3.5" /> Add category</button>
          </div>
        );
      },
    },
    {
      key: "diagnostic", label: "Diagnostic",
      render: ({ data }) => {
        const rows = paretoize(data.categories || []);
        const total = rows.reduce((a, r) => a + Number(r.frequency || 0), 0);
        const vital = rows.filter(r => r._vital);
        return (
          <div className="space-y-4" data-testid="pareto-diagnostic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Total {data.unit || "defects"}</p><p className="font-heading text-2xl font-bold text-sage-900">{total.toLocaleString()}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Categories</p><p className="font-heading text-2xl font-bold text-sage-900">{rows.length}</p></div>
              <div className="bg-green-50 rounded-md p-3 border border-green-200"><p className="text-xs uppercase tracking-wider font-semibold text-green-800">Vital Few (≤80%)</p><p className="font-heading text-2xl font-bold text-green-800">{vital.length}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Trivial Many</p><p className="font-heading text-2xl font-bold text-sage-700">{rows.length - vital.length}</p></div>
            </div>
            <div className="bg-white border border-sage-200 rounded-md p-3"><ParetoChart rows={rows} /></div>
            <table className="w-full text-xs border border-sage-100 rounded-md overflow-hidden">
              <thead className="bg-sage-50 text-sage-600 uppercase tracking-wider"><tr><th className="text-left px-2 py-1.5">#</th><th className="text-left px-2 py-1.5">Category</th><th className="text-right px-2 py-1.5">Freq</th><th className="text-right px-2 py-1.5">Share</th><th className="text-right px-2 py-1.5">Cum %</th><th className="text-left px-2 py-1.5">Group</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={`border-t border-sage-100 ${r._vital ? "bg-green-50/50" : ""}`}>
                    <td className="px-2 py-1 font-mono">{i + 1}</td>
                    <td className="px-2 py-1 font-semibold text-sage-900">{r.label || "—"}</td>
                    <td className="text-right px-2 py-1 font-mono">{r.frequency}</td>
                    <td className="text-right px-2 py-1 font-mono">{r._share.toFixed(1)}%</td>
                    <td className="text-right px-2 py-1 font-mono font-bold">{r._cum.toFixed(1)}%</td>
                    <td className="px-2 py-1"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${r._vital ? "bg-green-100 text-green-700" : "bg-sage-100 text-sage-600"}`}>{r._vital ? "Vital Few" : "Trivial"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button data-testid="export-pareto-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish Pareto" />;
}

export { paretoize };
